/**
 * Edit Profile Screen
 * Form to edit user profile information
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../store/onboardingStore';
import { usersApi } from '../api/endpoints/users';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import logger from '../utils/logger';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

export default function EditProfileScreen() {
  const navigation = useNavigation();
  const { user, setUser } = useAuthStore();
  const { currentStep } = useOnboardingStore();

  // Get first_name/last_name from authStore or onboardingStore profile
  const firstName = user?.first_name || currentStep?.profile?.first_name || '';
  const lastName = user?.last_name || currentStep?.profile?.last_name || '';

  const [formData, setFormData] = useState({
    firstName,
    lastName,
    email: user?.email || '',
    mobile: user?.phone_number || '',
    gender: user?.gender || '',
  });

  const [loading, setLoading] = useState(false);
  const [showGenderPicker, setShowGenderPicker] = useState(false);

  const genderOptions = [
    { label: 'Homme', value: 'male' },
    { label: 'Femme', value: 'female' },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      // Build payload — never send blank identity fields
      const payload: Record<string, string | undefined> = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        gender: formData.gender,
      };
      if (formData.email.trim()) {
        payload.email = formData.email.trim();
      }
      if (formData.mobile.trim()) {
        payload.phone_number = formData.mobile.trim();
      }

      const updatedUser = await usersApi.updateProfile({
        user: payload,
      });

      // Update user in auth store
      setUser({
        id: parseInt(updatedUser.user_id),
        username: updatedUser.username,
        email: updatedUser.email,
        phone_number: updatedUser.phone_number,
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        gender: updatedUser.gender,
      });

      Alert.alert('Succès', 'Votre profil a été mis à jour avec succès');
      navigation.goBack();
    } catch (error: any) {
      logger.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : 'Une erreur est survenue lors de la mise à jour';
      Alert.alert('Erreur', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleCancel} style={styles.backButton}>
          <Icon name="arrow-left" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Modifier le profil</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView style={styles.content}>
        {/* Profile Avatar */}
        <View style={styles.avatarSection}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {formData.firstName?.charAt(0) || 'U'}
              {formData.lastName?.charAt(0) || ''}
            </Text>
          </View>
          <TouchableOpacity style={styles.changePhotoButton}>
            <Text style={styles.changePhotoText}>Changer la photo</Text>
          </TouchableOpacity>
        </View>

        {/* Form Fields */}
        <View style={styles.form}>
          <View style={styles.formRow}>
            <View style={styles.formField}>
              <Text style={styles.label}>Prénom</Text>
              <Input
                value={formData.firstName}
                onChangeText={(text) => setFormData({ ...formData, firstName: text })}
                placeholder="Prénom"
              />
            </View>
            <View style={styles.formField}>
              <Text style={styles.label}>Nom</Text>
              <Input
                value={formData.lastName}
                onChangeText={(text) => setFormData({ ...formData, lastName: text })}
                placeholder="Nom"
              />
            </View>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Email</Text>
            <Input
              value={formData.email}
              onChangeText={(text) => setFormData({ ...formData, email: text })}
              placeholder="email@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Téléphone</Text>
            <Input
              value={formData.mobile}
              onChangeText={(text) => setFormData({ ...formData, mobile: text })}
              placeholder="+33 6 12 34 56 78"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Genre</Text>
            <TouchableOpacity
              style={styles.pickerButton}
              onPress={() => setShowGenderPicker(!showGenderPicker)}
            >
              <Text style={styles.pickerButtonText}>
                {genderOptions.find(opt => opt.value === formData.gender)?.label || 'Sélectionner'}
              </Text>
              <Icon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>

            {showGenderPicker && (
              <View style={styles.pickerOptions}>
                {genderOptions.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={styles.pickerOption}
                    onPress={() => {
                      setFormData({ ...formData, gender: option.value });
                      setShowGenderPicker(false);
                    }}
                  >
                    <Text style={styles.pickerOptionText}>{option.label}</Text>
                    {formData.gender === option.value && (
                      <Icon name="check" size={20} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actions}>
          <Button
            title="Enregistrer"
            onPress={handleSave}
            loading={loading}
            disabled={loading}
          />
          <TouchableOpacity onPress={handleCancel} style={styles.cancelButton}>
            <Text style={styles.cancelButtonText}>Annuler</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: {
    padding: spacing.xs,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text,
  },
  headerRight: {
    width: 40,
  },
  content: {
    flex: 1,
  },
  avatarSection: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  avatarText: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '600',
    color: '#fff',
  },
  changePhotoButton: {
    padding: spacing.sm,
  },
  changePhotoText: {
    fontSize: typography.fontSize.md,
    color: colors.primary,
  },
  form: {
    padding: spacing.lg,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  formField: {
    flex: 1,
  },
  formGroup: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: typography.fontSize.md,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  pickerButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  pickerOptions: {
    marginTop: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    ...Platform.select({
      web: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
    }),
  },
  pickerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  pickerOptionText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  actions: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  cancelButton: {
    padding: spacing.md,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
});
